import React, { useEffect, useState } from "react";
import { Check, Clock, Mail, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { UserAvatar } from "@/components/common";

export const STATUS = { apen: null, venter: { label: "Venter på svar", icon: Clock, cls: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]" }, avklart: { label: "Avklart", icon: Check, cls: "bg-[hsl(var(--v-ok)/0.12)] text-[hsl(var(--v-ok))]" } };

/** Only says something when there is something to say: open threads carry no pill. */
export function ThreadStatus({ status, className, testId }) {
  const s = STATUS[status];
  if (!s) return null;
  const Icon = s.icon;
  return <span className={cn("inline-flex h-[18px] shrink-0 items-center gap-1 rounded-full px-1.5 text-[10.5px] font-medium", s.cls, className)} data-testid={testId} data-status={status}><Icon className="h-2.5 w-2.5" strokeWidth={2.6} />{s.label}</span>;
}

export function Participants({ people = [], max = 3, size = "xs", className }) {
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <span className={cn("inline-flex items-center", className)} data-testid="thread-participants">
      {shown.map((p, i) => <UserAvatar key={p.id || i} name={p.name} initials={p.initials} color={p.color} src={p.avatar_url} size={size} className={cn("ring-2 ring-white", i > 0 && "-ml-1.5")} />)}
      {more > 0 ? <span className="-ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-black/[0.08] px-1 font-mono text-[10px] text-foreground/70 ring-2 ring-white">+{more}</span> : null}
    </span>
  );
}

export function KindMark({ kind, imported }) {
  if (kind !== "epost") return null;
  return <span className="inline-flex h-[18px] shrink-0 items-center gap-1 rounded-[4px] bg-black/[0.06] px-1.5 text-[10.5px] font-medium text-foreground/65" data-testid="thread-kind-epost"><Mail className="h-2.5 w-2.5" />{imported ? "Importert e-post" : "E-post"}</span>;
}

/** The channel, always beside the send button – so Egil never doubts who he is writing to. */
export function ChannelPill({ kind = "intern", className, testId = "composer-channel" }) {
  const epost = kind === "epost";
  const privat = kind === "privat";
  return (
    <span className={cn("inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium", epost ? "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]" : "bg-[hsl(var(--brand)/0.07)] text-[hsl(var(--brand-strong))]", className)} data-testid={testId} data-channel={kind} title={epost ? "Ekstern kanal – e-post" : privat ? "Privat – bare synlig for deltakerne" : "Internt i teamet – går ikke til KI eller kunde"}>
      {epost ? <Mail className="h-3 w-3" /> : <Lock className="h-3 w-3" />}{epost ? "E-post" : privat ? "Privat" : "Internt"}
    </span>
  );
}

const blobCache = new Map();

/** Attachments live behind auth – fetched as a blob, cached per url for the session. */
export function useBlobUrl(url, enabled = true) {
  const [src, setSrc] = useState(() => blobCache.get(url) || null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!url || !enabled) return undefined;
    if (blobCache.has(url)) { setSrc(blobCache.get(url)); return undefined; }
    let alive = true;
    api.get(url, { responseType: "blob" }).then((r) => { if (!alive) return; const u = URL.createObjectURL(r.data); blobCache.set(url, u); setSrc(u); }).catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [url, enabled]);
  return { src, error };
}

export function AuthImage({ url, alt, className, onClick, testId }) {
  const { src, error } = useBlobUrl(url);
  if (error) return <span className={cn("flex items-center justify-center rounded-[10px] bg-black/[0.04] text-[12px] text-muted-foreground", className)}>Kunne ikke hente bildet</span>;
  if (!src) return <span className={cn("shimmer block rounded-[10px]", className)} />;
  return <img src={src} alt={alt || ""} onClick={onClick} className={cn("block rounded-[10px] object-cover", onClick && "cursor-zoom-in", className)} draggable={false} data-testid={testId} />;
}
